import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  TablePagination,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Speed as SpeedIcon,
  PowerSettingsNew as PowerIcon,
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const AdminPerfPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [endpointPage, setEndpointPage] = useState(0);
  const [userPage, setUserPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const getToken = () =>
    localStorage.getItem('rv_access_token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('id_token');

  const headers = () => {
    const t = getToken();
    return {
      'Content-Type': 'application/json',
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    };
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/api/debug/perf/stats`, { headers: headers() });
      if (!res.ok) throw new Error(`Failed to load stats (${res.status})`);
      const data = await res.json();
      setStats(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadExport = async () => {
    try {
      setBusy(true);
      setError(null);
      const res = await fetch(`${API_BASE}/api/debug/perf/export?limit=2000`, { headers: headers() });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `perf-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleTracking = async () => {
    try {
      setBusy(true);
      setError(null);
      const res = await fetch(`${API_BASE}/api/debug/perf/toggle`, {
        method: 'POST',
        headers: headers(),
      });
      if (!res.ok) throw new Error(`Toggle failed (${res.status})`);
      const data = await res.json();
      alert(`Tracking is now ${data.tracking ? 'ON' : 'OFF'}.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const clearLogs = async () => {
    if (!window.confirm('Delete ALL performance logs? This cannot be undone.')) return;
    try {
      setBusy(true);
      setError(null);
      const res = await fetch(`${API_BASE}/api/debug/perf/clear`, {
        method: 'POST',
        headers: headers(),
      });
      if (!res.ok) throw new Error(`Clear failed (${res.status})`);
      const data = await res.json();
      alert(`Deleted ${data.deleted} log entries.`);
      fetchStats();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const colorForAvg = (ms) => {
    if (ms == null) return 'default';
    if (ms > 1000) return 'error';
    if (ms > 500) return 'warning';
    return 'success';
  };

  const formatSize = (bytes) => {
    if (bytes == null) return '—';
    if (bytes < 1024) return `${Math.round(bytes)} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          <SpeedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Performance Tracking
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={fetchStats} disabled={loading || busy}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={downloadExport}
          disabled={busy || !stats?.total_logs}
        >
          Download Data (JSON)
        </Button>
        <Button
          variant="outlined"
          startIcon={<PowerIcon />}
          onClick={toggleTracking}
          disabled={busy}
        >
          Toggle Tracking
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={clearLogs}
          disabled={busy || !stats?.total_logs}
        >
          Clear Logs
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : !stats ? (
        <Typography variant="body2" color="text.secondary">
          No data available.
        </Typography>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Total logged requests: <strong>{stats.total_logs ?? 0}</strong>
          </Typography>

          <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>
            By Endpoint (sorted slowest first)
          </Typography>
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Path</strong></TableCell>
                  <TableCell align="right"><strong>Count</strong></TableCell>
                  <TableCell align="right"><strong>Avg ms</strong></TableCell>
                  <TableCell align="right"><strong>Max ms</strong></TableCell>
                  <TableCell align="right"><strong>Avg Size</strong></TableCell>
                  <TableCell align="right"><strong>Max Size</strong></TableCell>
                  <TableCell align="right"><strong>Slow (&gt;1s)</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(stats.by_endpoint || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary">
                        No endpoint data yet. Use the app to generate some requests.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  stats.by_endpoint
                    .slice(endpointPage * rowsPerPage, endpointPage * rowsPerPage + rowsPerPage)
                    .map((row, i) => (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row._id}</TableCell>
                        <TableCell align="right">{row.count}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={row.avg_ms ?? '—'}
                            size="small"
                            color={colorForAvg(row.avg_ms)}
                          />
                        </TableCell>
                        <TableCell align="right">{row.max_ms ?? '—'}</TableCell>
                        <TableCell align="right">{formatSize(row.avg_size)}</TableCell>
                        <TableCell align="right">{formatSize(row.max_size)}</TableCell>
                        <TableCell align="right">{row.slow_count || 0}</TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={(stats.by_endpoint || []).length}
              page={endpointPage}
              onPageChange={(_, p) => setEndpointPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setEndpointPage(0);
                setUserPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </TableContainer>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            By User
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Username</strong></TableCell>
                  <TableCell align="right"><strong>Count</strong></TableCell>
                  <TableCell align="right"><strong>Avg ms</strong></TableCell>
                  <TableCell align="right"><strong>Max ms</strong></TableCell>
                  <TableCell align="right"><strong>Slow (&gt;1s)</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(stats.by_user || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No user data yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  stats.by_user
                    .slice(userPage * rowsPerPage, userPage * rowsPerPage + rowsPerPage)
                    .map((row, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{row._id}</TableCell>
                        <TableCell align="right">{row.count}</TableCell>
                        <TableCell align="right">{row.avg_ms ?? '—'}</TableCell>
                        <TableCell align="right">{row.max_ms ?? '—'}</TableCell>
                        <TableCell align="right">{row.slow_count || 0}</TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={(stats.by_user || []).length}
              page={userPage}
              onPageChange={(_, p) => setUserPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setEndpointPage(0);
                setUserPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </TableContainer>
        </>
      )}
    </Paper>
  );
};

export default AdminPerfPanel;
